import moment from 'moment'
import { useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { Checkbox } from '../common/fields/Checkbox'
import { SearchableSelect } from '../common/fields/SearchableSelect'

type Props = {
    isOpen: boolean
    onClose: () => void
    onBack?: () => void
    onChange?: (weekdays: Weekday[]) => void
    onDone: (weekdays: Weekday[]) => void
    description?: string
    currentAvailability: Weekday[]
    title: string
    shouldHideTimePickers?: boolean
}

export type ItemOption = {
    value: number
    name: string
}

export type Weekday = {
    name: string
    enabled: boolean
    start: ItemOption
    end: ItemOption
    endHours: ItemOption[]
}

export const createAvailabilitySummary = (weekdays: Weekday[], fallback?: string): string => {
    const days: string[] = []
    weekdays.forEach((weekday) => {
        if (weekday.enabled) {
            days.push(weekday.name)
        }
    })

    if (days.length === 0) {
        return fallback ?? 'Same as business'
    }

    return days.join(', ')
}

export const createAvailabilityArray = (data: {
    schedule?: number
    monStartHour?: number
    monEndHour?: number
    tueStartHour?: number
    tueEndHour?: number
    wedStartHour?: number
    wedEndHour?: number
    thuStartHour?: number
    thuEndHour?: number
    friStartHour?: number
    friEndHour?: number
    satStartHour?: number
    satEndHour?: number
    sunStartHour?: number
    sunEndHour?: number
}): Weekday[] => {
    const itemHours: ItemOption[] = []
    for (let i = 0; i < 24; i++) {
        itemHours.push({ value: i, name: moment().hour(i).minute(0).format('h:mm A') })
        itemHours.push({ value: i + 0.5, name: moment().hour(i).minute(30).format('h:mm A') })
    }

    itemHours.push({ value: 24, name: moment().hour(0).minute(0).format('h:mm A') })
    const weekdays: Weekday[] = []
    const dayKeys = [
        { start: 'monStartHour', end: 'monEndHour' },
        { start: 'tueStartHour', end: 'tueEndHour' },
        { start: 'wedStartHour', end: 'wedEndHour' },
        { start: 'thuStartHour', end: 'thuEndHour' },
        { start: 'friStartHour', end: 'friEndHour' },
        { start: 'satStartHour', end: 'satEndHour' },
        { start: 'sunStartHour', end: 'sunEndHour' },
    ] as const

    for (let i = 1; i <= 7; i++) {
        const keys = dayKeys[i - 1]
        const start =
            itemHours.find((option) => option.value === data[keys.start]) ?? ITEM_HOURS[16]
        const end = itemHours.find((option) => option.value === data[keys.end]) ?? ITEM_HOURS[36]
        const enabled = !!start && !!data.schedule && (data.schedule & (1 << (i - 1))) !== 0

        weekdays.push({
            name: moment().day(i).format('ddd'),
            enabled,
            start,
            end,
            endHours: [],
        })
    }

    return weekdays
}

export const generateItemHours = () => {
    const hours: ItemOption[] = []
    for (let i = 0; i < 24; i++) {
        hours.push({ value: i, name: moment().hour(i).minute(0).format('h:mm A') })
        hours.push({ value: i + 0.5, name: moment().hour(i).minute(30).format('h:mm A') })
    }
    hours.push({ value: 24, name: moment().hour(0).minute(0).format('h:mm A') })
    return hours
}

const initializeWeekdays = (initialAvailability: Weekday[]) => {
    const days: Weekday[] = []

    for (let weekday = 1; weekday <= 7; weekday++) {
        let defaultEnabled = false
        let defaultStart = ITEM_HOURS[16]
        let defaultEnd: ItemOption | string = ITEM_HOURS[36]
        let defaultEndHours = ITEM_HOURS.slice(20)

        if (initialAvailability.length >= weekday) {
            const dayAvailability = initialAvailability[weekday - 1]
            if (dayAvailability.enabled) {
                defaultEnabled = dayAvailability.enabled
                defaultStart = dayAvailability.start
                defaultEnd = dayAvailability.end
            }
        }

        days.push({
            name: moment().day(weekday).format('ddd'),
            enabled: defaultEnabled,
            start: defaultStart,
            end: defaultEnd,
            endHours: defaultEndHours,
        })
    }
    return days
}

const ITEM_HOURS = generateItemHours()

export const SetupBusinessHoursModal = ({
    isOpen,
    onClose,
    onBack,
    onDone,
    onChange,
    currentAvailability,
    description,
    title,
    shouldHideTimePickers = false,
}: Props) => {
    const [weekdays, setWeekdays] = useState(() => initializeWeekdays(currentAvailability))

    interface WeekdayUpdates {
        enabled?: boolean
        start?: ItemOption
        end?: ItemOption
        endHours?: ItemOption[]
    }

    const handleWeekdayTimeChange = (weekdayIndex: number, updates: WeekdayUpdates): void => {
        const updatedWeekdays = weekdays.map((weekday, index) => {
            if (index !== weekdayIndex) return weekday

            const updatedWeekday: Weekday = { ...weekday, ...updates }
            if (updates.start) {
                const selection = updates.start
                const itemHoursIndex = ITEM_HOURS.findIndex((i) => i.value === selection.value)
                // Make the endHours be at least 2 hours more than selected start time
                const endHourStartIndex = Math.min(itemHoursIndex + 4, ITEM_HOURS.length - 1)
                updatedWeekday.endHours = ITEM_HOURS.slice(endHourStartIndex)

                // Update end time if it's no longer valid
                if (updatedWeekday.end.value <= selection.value) {
                    const newEndHour = ITEM_HOURS.find((i) => i.value === selection.value + 2)
                    if (newEndHour) updatedWeekday.end = newEndHour
                }
            }
            return updatedWeekday
        })

        setWeekdays(updatedWeekdays)
        onChange?.(updatedWeekdays)
    }

    return (
        <Modal
            title={title}
            isOpen={isOpen}
            onClose={onClose}
            onNavigateBack={onBack}
            footer={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button intent="primary" onClick={() => onDone(weekdays)}>
                        Done
                    </Button>
                </div>
            }
        >
            {!!description && (
                <Paragraph size="md" className="mb-6 text-gray-600">
                    {description}
                </Paragraph>
            )}

            <div className="space-y-3">
                {weekdays.map((weekday, index) => (
                    <div
                        key={weekday.name}
                        className={twm(
                            'flex h-[82px] w-full cursor-pointer flex-col justify-between rounded-lg border',
                            weekday.enabled ? 'border-secondary-400 border-2' : 'border-gray-300',
                            'p-4 text-left hover:bg-black/4'
                        )}
                        onClick={() =>
                            handleWeekdayTimeChange(index, { enabled: !weekday.enabled })
                        }
                    >
                        <div className="flex h-full w-full items-center justify-start gap-x-4">
                            <Checkbox
                                isChecked={weekday.enabled}
                                onChange={() =>
                                    handleWeekdayTimeChange(index, { enabled: !weekday.enabled })
                                }
                            />
                            <Label className="grow text-gray-700">{weekday.name}</Label>

                            {shouldHideTimePickers ? null : !weekday.enabled ? (
                                <div className="text-gray-600">Unavailable</div>
                            ) : (
                                <div className="flex items-center">
                                    <SearchableSelect
                                        size="medium"
                                        shouldPortal={false}
                                        className="w-[153px] border-gray-400"
                                        items={ITEM_HOURS.slice(0, ITEM_HOURS.length - 4).map(
                                            (option) => ({
                                                id: String(option.value),
                                                title: option.name,
                                            })
                                        )}
                                        onSelectItem={(item) => {
                                            if (item === null) return
                                            handleWeekdayTimeChange(index, {
                                                start: {
                                                    name: item.title,
                                                    value: Number(item.id),
                                                },
                                            })
                                        }}
                                        selectedItemID={weekday.start.value.toString()}
                                        shouldPreserveLastSelection
                                    />

                                    <span className="mx-3 text-gray-700">-</span>

                                    <SearchableSelect
                                        size="medium"
                                        shouldPortal={false}
                                        className="w-[153px] border-gray-400"
                                        items={weekday.endHours.map((option) => ({
                                            id: String(option.value),
                                            title: option.name,
                                        }))}
                                        onSelectItem={(item) => {
                                            if (item === null) return
                                            handleWeekdayTimeChange(index, {
                                                end: {
                                                    name: item.title,
                                                    value: Number(item.id),
                                                },
                                            })
                                        }}
                                        selectedItemID={weekday.end.value.toString()}
                                        shouldPreserveLastSelection
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </Modal>
    )
}
